import { SubFormProps } from '@forms/Components/types';
import { FormikErrors, FormikValues } from 'formik';
import { useEffect } from 'react';

/**
 *  Hook to extend Formik functionality for use with Sub-Forms by updating the values and errors
 *  of the sub-forms fields on the value of parent form.
 */
export const useFormikValues = ({
	name,
	subFormProps: {
		errors: subFormErrors,
		setFieldError: setSubFormFieldError,
		setSubmitting: setSubFormSubmitting,
		resetForm: resetSubForm,
		values: subFormValues
	},
	parentFormProps: {
		errors: parentErrors,
		isSubmitting: parentIsSubmitting,
		initialValues: parentInitialValues,
		setFieldValue: setParentFieldValue,
		setFieldError: setParentFieldError
	}
}: SubFormProps) => {
	/** Anytime the parent form errors change,  */
	useEffect(
		function propogateParentErrors() {
			/** If the parent form has errors for us, update our fields */
			if (!isFormikErrorsEmpty(parentErrors)) {
				const record = parentErrors as Record<string, string>;

				/** See if any of the parent errors are for this subform */
				const subFormErrors = Object.entries(record).find((entry) => entry[0] === name);

				if (subFormErrors) {
					/** Errors propagating down will be formatted as <fieldName1>:<fieldError1>;<fieldName2>:<fieldError2>;
					 *  Otherwise, the error was sent up from the subform and does not need to be addressed again.
					 */
					if (subFormErrors[1].includes(';')) {
						const fieldErrors = subFormErrors[1].split(';');

						fieldErrors.forEach((error) => {
							if (!error) {
								return;
							}
							const [subField, subFieldError] = error.split(':');
							setSubFormFieldError(subField, subFieldError);
						});
					}
				}
			}
		},
		[name, parentErrors, setSubFormFieldError]
	);

	useEffect(
		function bubbleSubformErrors() {
			// If the subform has errors bubble them to the parent
			if (!isFormikErrorsEmpty(subFormErrors)) {
				setParentFieldError(name, 'Form is in an error state');
			} else {
				setParentFieldError(name, undefined);
			}

			// Bubble the form values
			void setParentFieldValue(name, { ...subFormValues }, false);
		},
		[name, subFormValues, subFormErrors, setParentFieldError, setParentFieldValue]
	);

	/** When the parent isSubmitting flag changes, update the sub-form isSubmitting flag manually */
	useEffect(() => {
		setSubFormSubmitting(parentIsSubmitting);
	}, [parentIsSubmitting, setSubFormSubmitting]);

	/** When the parent form was submitted and the initial values were updated,
	 * we need to reset the sub-form with the newest values to remove dirty class etc.
	 */
	useEffect(() => {
		/** We need the specific initial values for this subform out of the parent inital values */
		const asRecord = parentInitialValues as Record<string, FormikValues>;
		resetSubForm({ values: asRecord[name] });
	}, [parentInitialValues, resetSubForm, name]);
};

/**
 * Boolean indicating that the Formik Errors object is empty.
 *  True meaning it is empty. False meaning it is not empty.
 */
export function isFormikErrorsEmpty(obj: FormikErrors<FormikValues>) {
	return Object.keys(obj).reduce((isEmpty: boolean, key) => {
		if (obj[key] || !isEmpty) {
			return false;
		}
		return true;
	}, true);
}
